import React from 'react';
// import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Divider,
} from '@mui/material';

import suboxoneLogo from '../assets/suboxone/suboxone-logo.svg';
import treatmentMethod from '../assets/suboxone/suboxone_treatment.png';
import suboxoneBackground from '../assets/suboxone/suboxone-background.webp';
const zocdocLink =
  'https://www.zocdoc.com/practice/aspire-bhp-179924?lock=true&isNewPatient=false&referrerType=widget';
 
const highlights = [
  'FDA-approved treatment for opioid use disorder',
  'Helps reduce cravings and withdrawal symptoms',
  'Part of a comprehensive recovery plan',
  'Medication management and ongoing support',
  'Personalized treatment plans',
  'Focused on long-term recovery and stability',
];

const pathway = [
  {
    title: 'Assessment & Intake',
    description:
      'Our team completes a comprehensive evaluation including medical history, substance use history, and treatment needs.',
  },
  {
    title: 'Induction & Stabilization',
    description:
      'Suboxone® treatment is carefully started and adjusted to help reduce withdrawal symptoms and cravings safely.',
  },
  {
    title: 'Ongoing Recovery Support',
    description:
      'Regular follow-up appointments, medication management, and supportive care help support long-term recovery.',
  },
];

const infoCards = [
  {
    title: 'What is Suboxone®?',
    description:
      'Suboxone® (buprenorphine/naloxone) is an FDA-approved medication used to treat opioid use disorder (OUD). It works by helping reduce cravings and withdrawal symptoms without producing the same “high” associated with other opioids.',
    image: suboxoneLogo,
  },
  {
    title: 'How is Treatment Administered?',
    description:
      'Suboxone® may be prescribed as a film or tablet that dissolves under the tongue or inside the cheek. Treatment begins with a carefully monitored induction phase followed by ongoing maintenance and medication management visits.',
    image: treatmentMethod,
  },
];

const safetyInfo = [
  'Suboxone® may cause drowsiness or respiratory depression, especially when combined with alcohol or sedatives.',
  'It should only be taken exactly as prescribed by your healthcare provider.',
  'Stopping Suboxone® suddenly may lead to withdrawal symptoms.',
  'Patients should be monitored closely during treatment, especially during early stages and dose adjustments.',
  'Medication-assisted treatment is most effective when combined with supportive care and behavioral treatment approaches.',
];

export default function Suboxone() {
  return (
    <>
      <Helmet>
        <title>Suboxone® Treatment | Aspire Behavioral Health</title>
        <meta
          name="description"
          content="Aspire Behavioral Health provides Suboxone® treatment for opioid use disorder with medication management, recovery support, and personalized treatment planning."
        />
      </Helmet>

      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',

          minHeight: {
            xs: 520,
            sm: 580,
            md: 650,
            lg: 620,
          },

          py: {
            xs: 8,
            sm: 10,
            md: 12,
            lg: 10,
          },

          overflow: 'hidden',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          color: 'white',
        }}
      >
        <Box
          component="img"
          src={suboxoneBackground}
          alt="Suboxone treatment at Aspire Behavioral Health"
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
            zIndex: -2,
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.45)',
            zIndex: -1,
          }}
        />

        <Box
          sx={{
            position: 'relative',
            zIndex: 2,
            px: 3,
            maxWidth: '850px',
          }}
        >
          <Typography
            variant="h2"
            sx={{
              fontWeight: 600,
              mb: 2,
              fontSize: { xs: '2.2rem', sm: '3.5rem' },
              lineHeight: 1.2,
            }}
          >
            Suboxone® Treatment
          </Typography>

          <Typography
            variant="h6"
            sx={{
              fontWeight: 300,
              lineHeight: 1.8,
              mb: 4,
              fontSize: { xs: '1rem', sm: '1.2rem' },
            }}
          >
            Compassionate, evidence-based treatment and support for opioid use
            disorder and long-term recovery.
          </Typography>

          <Box
            sx={{
              display: 'flex',
              gap: 2,
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <Button
              variant="contained"
              href="tel:2108669850"
              sx={{
                backgroundColor: '#e3f3c5',
                color: 'black',
                fontWeight: 600,
                px: 4,
                py: 1.3,
                borderRadius: '30px',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#d3eab0',
                },
              }}
            >
              Call 210-866-9850
            </Button>

            <Button
                          component="a"
                          href={zocdocLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="outlined"
                          sx={{
                            borderColor: 'white',
                            color: 'white !important',
                            fontWeight: 600,
                            px: 4,
                            py: 1.3,
                            borderRadius: '30px',
                            textTransform: 'none',
                            '&:visited': {
                              color: 'white',
                            },
                            '&:hover': {
                              borderColor: 'white',
                              backgroundColor: 'rgba(255,255,255,0.1)',
                              color: 'white',
                            },
                          }}
                        >
                          Schedule an Appointment
                        </Button>
          </Box>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ backgroundColor: 'white', py: 8, px: 2 }}>
        {/* Intro */}
        <Box sx={{ textAlign: 'center', maxWidth: '900px', mx: 'auto', mb: 7 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>
            Our Suboxone® Program
          </Typography>

          <Typography
            variant="body1"
            sx={{
              lineHeight: 1.9,
              color: 'text.secondary',
              fontSize: { xs: '1rem', sm: '1.05rem' },
            }}
          >
            At Aspire Behavioral Health, we provide evidence-based treatment for
            opioid use disorder (OUD) using Suboxone®
            (buprenorphine/naloxone).
            <br />
            <br />
            Suboxone® helps reduce cravings and withdrawal symptoms so patients
            can focus on recovery while rebuilding stability in their lives. Our
            treatment program combines medication management with supportive care
            and personalized recovery planning.
            <br />
            <br />
            We work closely with patients, families, and referring providers
            when appropriate to support long-term wellness and recovery.
            <br />
            <br />
            To learn more or schedule an evaluation, call{' '}
            <a href="tel:2108669850">210-866-9850</a>.
          </Typography>
        </Box>

        <Divider
          sx={{
            width: { xs: '80%', sm: '60%', md: '40%' },
            mx: 'auto',
            mb: 7,
            borderColor: '#d9d9d9',
          }}
        />

        {/* Highlights */}
        <Box
          sx={{
            backgroundColor: '#e3f3c5',
            color: 'black',
            maxWidth: '1200px',
            mx: 'auto',
            p: { xs: 3, sm: 5 },
            borderRadius: 4,
            boxShadow: '0px 4px 12px rgba(0,0,0,0.12)',
            mb: 8,
          }}
        >
          <Box
            component="img"
            src={suboxoneLogo}
            alt="Suboxone Logo"
            sx={{
              display: 'block',
              mx: 'auto',
              width: '100%',
              maxWidth: 250,
              mb: 3,
            }}
          />

          <Typography
            variant="h4"
            sx={{ textAlign: 'center', fontWeight: 600, mb: 2 }}
          >
            Suboxone® at a Glance
          </Typography>

          <Typography
            variant="body1"
            sx={{
              textAlign: 'center',
              maxWidth: '750px',
              mx: 'auto',
              mb: 4,
              lineHeight: 1.7,
            }}
          >
            Medication-assisted treatment can help support recovery, reduce
            cravings, and improve stability throughout the healing process.
          </Typography>

          <Grid container spacing={2} justifyContent="center" alignItems="stretch">
            {highlights.map((item) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                key={item}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Card
                  sx={{
                    width: '100%',
                    maxWidth: 340,
                    borderRadius: 3,
                    boxShadow: 'none',
                    backgroundColor: 'rgba(255,255,255,0.75)',
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', p: 2.5 }}>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {item}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Info Cards */}
        <Box sx={{ maxWidth: '1000px', mx: 'auto', mb: 8 }}>
          <Typography
            variant="h4"
            sx={{
              textAlign: 'center',
              fontWeight: 600,
              mb: 2,
            }}
          >
            About Treatment
          </Typography>

          <Typography
            variant="body1"
            sx={{
              textAlign: 'center',
              maxWidth: '750px',
              mx: 'auto',
              mb: 5,
              lineHeight: 1.8,
              color: 'text.secondary',
            }}
          >
            Our goal is to provide supportive, individualized care that helps
            patients move toward long-term recovery and improved quality of life.
          </Typography>

          {infoCards.map((item) => (
            <Card
              key={item.title}
              sx={{
                borderRadius: 4,
                boxShadow: '0px 4px 14px rgba(0,0,0,0.1)',
                mb: 4,
              }}
            >
              <CardContent
                sx={{
                  p: { xs: 3, sm: 5 },
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                {item.image && (
                  <Box
                    component="img"
                    src={item.image}
                    alt={item.title}
                    sx={{
                      width: '100%',
                      maxWidth: 260,
                      height: 'auto',
                      borderRadius: 3,
                      objectFit: 'contain',
                    }}
                  />
                )}

                <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                    {item.title}
                  </Typography>

                  <Typography sx={{ lineHeight: 1.9, color: 'text.secondary' }}>
                    {item.description}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* Care Pathway */}
        <Box
          sx={{
            maxWidth: '1100px',
            mx: 'auto',
            mb: 8,
          }}
        >
          <Typography
            variant="h4"
            sx={{
              textAlign: 'center',
              fontWeight: 600,
              mb: 2,
            }}
          >
            The Recovery Pathway
          </Typography>

          <Typography
            variant="body1"
            sx={{
              textAlign: 'center',
              maxWidth: '750px',
              mx: 'auto',
              mb: 5,
              lineHeight: 1.8,
              color: 'text.secondary',
            }}
          >
            Recovery is a process, and our team supports you through each stage
            of treatment and ongoing care.
          </Typography>

          <Grid container spacing={3} justifyContent="center">
            {pathway.map((step, index) => (
              <Grid item xs={12} sm={6} md={4} key={step.title}>
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: 4,
                    boxShadow: '0px 4px 14px rgba(0,0,0,0.1)',
                  }}
                >
                  <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        color: '#3a4c17',
                      }}
                    >
                      Step {index + 1}
                    </Typography>

                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      {step.title}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ lineHeight: 1.8, color: 'text.secondary' }}
                    >
                      {step.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Safety Information */}
        <Box
          sx={{
            maxWidth: '1000px',
            mx: 'auto',
            mb: 8,
          }}
        >
          <Card
            sx={{
              borderRadius: 4,
              boxShadow: '0px 4px 14px rgba(0,0,0,0.1)',
              backgroundColor: '#f7f7f7',
            }}
          >
            <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
              <Typography
                variant="h4"
                sx={{
                  textAlign: 'center',
                  fontWeight: 600,
                  mb: 3,
                }}
              >
                Important Safety Information
              </Typography>

              <Grid container spacing={2}>
                {safetyInfo.map((item) => (
                  <Grid item xs={12} key={item}>
                    <Card
                      sx={{
                        borderRadius: 3,
                        boxShadow: 'none',
                        backgroundColor: 'white',
                      }}
                    >
                      <CardContent sx={{ p: 2.5 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            lineHeight: 1.7,
                            color: 'text.secondary',
                          }}
                        >
                          {item}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Box>

        {/* Bottom CTA */}
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
            Ready to learn more about treatment options?
          </Typography>

          <Box
            sx={{
              display: 'flex',
              gap: 2,
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <Button
              variant="contained"
              href="tel:2108669850"
              sx={{
                backgroundColor: '#e3f3c5',
                color: 'black',
                fontWeight: 600,
                px: 4,
                py: 1.3,
                borderRadius: '30px',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#d3eab0',
                },
              }}
            >
              Call 210-866-9850
            </Button>

            <Button
                            component="a"
                            href={zocdocLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="outlined"
                            sx={{
                              backgroundColor: 'transparent',
                              border: '2px solid #3a4c17',
                              color: '#3a4c17 !important',
                              fontWeight: 600,
                              px: 4,
                              py: 1.3,
                              borderRadius: '30px',
                              textTransform: 'none',
                              '&:visited': {
                                color: '#3a4c17',
                              },
                              '&:hover': {
                                backgroundColor: 'rgba(58,76,23,0.05)',
                                border: '2px solid #3a4c17',
                                color: '#3a4c17',
                              },
                            }}
                          >
                            Schedule an Appointment
                          </Button>
          </Box>
        </Box>
      </Box>
    </>
  );
}